import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import {
  useAuthStore,
  useBusinessUnitStore,
  useCorporationStore,
} from "@type/store";
import { checkHasAccess } from "helpers/checkHasAccess";
import { getAccessRightByAccessType } from "helpers/getAccessRightByAccessType";

import { EAccessType } from "types";

type UseGetAccessProps = {
  requiredAccessRights?: number[];
  superAdminOnly?: boolean;
  type?: EAccessType;
};

export const useGetAccess = ({
  requiredAccessRights,
  type,
  superAdminOnly,
}: UseGetAccessProps) => {
  const location = useLocation();
  const buAccessRight = useBusinessUnitStore((state) => state.buAccessRight);
  const buAppAccessRight = useBusinessUnitStore(
    (state) => state.buAppAccessRight
  );
  const loadingBU = useBusinessUnitStore((state) => state.isLoading);
  const corpAccessRight = useCorporationStore((state) => state.corpAccessRight);
  const loadingCorp = useCorporationStore((state) => state.isLoading);
  const user = useAuthStore((state) => state.user);

  const [accessType, setAccessType] = useState<EAccessType>();
  const [givenAccess, setGivenAccess] = useState<boolean>();

  const { corporationSlug } = useParams();

  useEffect(() => {
    const paths = location.pathname.split("/");

    if (type) {
      setAccessType(type);
    } else if (paths?.[1]) {
      switch (paths[1]) {
        case EAccessType.COM:
          setAccessType(EAccessType.COM);
          break;
        case EAccessType.BU:
          setAccessType(EAccessType.BU);
          break;
        case EAccessType.CORP:
          setAccessType(EAccessType.CORP);
          break;
        case EAccessType.SEARCH:
          setAccessType(EAccessType.SEARCH);
          break;
        default:
          setAccessType(EAccessType.EMPTY);
      }
    }
  }, [location, corporationSlug, type]);

  useEffect(() => {
    if (superAdminOnly && !user.isSuperAdmin) {
      setGivenAccess(false);
    } else if (requiredAccessRights?.length) {
      switch (accessType) {
        case EAccessType.CORP:
          if (corpAccessRight !== undefined) {
            setGivenAccess(
              checkHasAccess(
                requiredAccessRights,
                getAccessRightByAccessType({
                  accessType,
                  corporationAccessRight: corpAccessRight,
                })
              )
            );
          }
          break;
        case EAccessType.BU:
          if (buAccessRight) {
            if (buAccessRight !== undefined) {
              setGivenAccess(
                checkHasAccess(
                  requiredAccessRights,
                  getAccessRightByAccessType({
                    accessType,
                    buAccessRight: {
                      ...buAccessRight,
                      ...buAppAccessRight,
                    },
                  })
                )
              );
            }
          }
          break;
        case EAccessType.COM:
          // TODO: Handle community ACR
          setGivenAccess(true);
          break;
        case EAccessType.NULL:
          setGivenAccess(false);
      }
    } else {
      setGivenAccess(true);
    }
  }, [
    requiredAccessRights,
    superAdminOnly,
    accessType,
    buAccessRight,
    loadingBU,
    loadingCorp,
    corpAccessRight,
    user.isSuperAdmin,
    buAppAccessRight,
  ]);

  return {
    accessType,
    loading: givenAccess === undefined,
    givenAccess,
  };
};
